import {
  type ChildAvailabilityItem,
  type ChildAvailabilitySearchResults,
  type ChildDetails,
  type ChildHoldItem,
  type ChildHoldResults,
  type ChildQueryRequestItem,
} from '@sola/schema/dotcom/children/child.js';
import { type ChildGlobalId } from '@sola/schema/dotcom/children/childUtilityInterfaces.js';
import {
  BeneficiaryAvailabilityCheckState,
  type HoldCreateUpdateRequest,
  type HoldReleaseRequest,
  type RemoveHoldResponse,
} from '@sola/schema/dotcom/children/hold.js';
import { AnyObject } from '@sola/schema/dotcom/helpers.js';
import { GivingHistory } from '@sola/schema/dotcom/proxy-api/givingHistory.js';
import { SourceCodeLookupResponse } from '@sola/schema/dotcom/proxy-api/sourceCodeLookup.js';
import { Supporter } from '@sola/schema/dotcom/proxy-api/supporter.js';
import { TaxReceipts } from '@sola/schema/dotcom/proxy-api/taxReceipts.js';
import { userErrorAvailabilityHold } from '@sola/shared-utils/errors/am.js';
import { userErrorCartMultiHoldFailed } from '@sola/shared-utils/errors/cart.js';
import {
  GetOptions,
  httpDelete,
  httpGet,
  httpGetWithStatus,
  httpPatch,
  httpPost,
  isUserError,
} from '@sola/shared-utils/httpClient.js';
import { isTwoHundredRes } from '@sola/shared-utils/misc.js';

import {
  ChildRichMixQueryParameters,
  type SearchChildQueryParameters,
  SupporterURLAttributes,
} from '../types/requests';

// TODO: likely shouldn't fall-through to data.devint
const proxyApiBase =
  process.env.PROXY_API_PROXY_DESTINATION ||
  process.env.NEXT_PUBLIC_BASE_URL_PROXY_API ||
  process.env.BASE_URL_PROXY_API ||
  'https://data.devint.compassion.com';
const supporterRouteBase = `${proxyApiBase}/supporters`;
const childrenRouteBase = `${proxyApiBase}/children`;
const sourceCodeRouteBase = `${proxyApiBase}/internal/source-codes`;

const corsOptions: GetOptions = {
  credentials: 'include',
};

export const constructURLWithQueryParams = (
  baseURL: string,
  queryParams: AnyObject = {},
) => {
  const url = new URL(baseURL);

  // Iterate over each query parameter using forEach
  Object.entries(queryParams).forEach(([param, value]) => {
    // Check if the query parameter is not null or undefined
    if (value !== null && value !== undefined) {
      // Append the query parameter to the URL
      url.searchParams.append(param, value);
    }
  });

  // Return the constructed URL
  return url.toString();
};

// TODO: Need to data-shape the responses
// TODO: Should try to have all of the responses include their types
export const getSupporterById = async (
  supporterId: string,
): Promise<Supporter | undefined> => {
  return await httpGet<Supporter>(
    `${supporterRouteBase}/${supporterId}`,
    corsOptions,
  );
};

export const getSupporterByIdWithOptions = async (
  supporterId: string,
  options: GetOptions,
): Promise<Supporter | undefined> => {
  return await httpGet<Supporter>(
    `${supporterRouteBase}/${supporterId}`,
    options,
  );
};

export const getSupporterAttribute = async <T>(
  supporterId: string,
  supporterAttribute: SupporterURLAttributes,
) => {
  return await httpGet<T>(
    `${supporterRouteBase}/${supporterId}/${supporterAttribute}`,
    corsOptions,
  );
};

export const getChildDetails = async (
  globalId: string,
): Promise<ChildDetails | undefined> => {
  return await httpGet<ChildDetails>(`${childrenRouteBase}/${globalId}`);
};

export const getChildAvailabilityDetails = async (
  globalId: ChildGlobalId['globalId'],
): Promise<ChildAvailabilityItem | undefined> => {
  return await httpGet<ChildAvailabilityItem>(
    `${childrenRouteBase}/${globalId}/availability`,
  );
};

export const getChildAvailabilityCheck = async (
  globalId: string,
  sourceCode?: string,
) => {
  return await httpGetWithStatus<{
    status: BeneficiaryAvailabilityCheckState;
  }>(
    `${childrenRouteBase}/${globalId}/check${sourceCode ? `?sourceCode=${sourceCode}` : ''}`,
  );
};

export const getChildRichMix = async (
  queryParams?: ChildRichMixQueryParameters,
) => {
  const searchUrl = constructURLWithQueryParams(
    `${childrenRouteBase}/richmix`,
    queryParams,
  );

  return await httpGet<ChildAvailabilitySearchResults>(searchUrl, corsOptions);
};

export const childAvailabilitySearch = async (
  queryParams?: SearchChildQueryParameters,
): Promise<ChildAvailabilitySearchResults | undefined> => {
  const searchUrl = constructURLWithQueryParams(`${childrenRouteBase}/search`, {
    ...queryParams,
    ...(queryParams?.countries && {
      countries: queryParams?.countries.join(','),
    }),
  });
  return await httpGet<ChildAvailabilitySearchResults>(searchUrl, corsOptions);
};

export const postChildAvailabilityQuery = async (
  payload: ChildQueryRequestItem,
): Promise<ChildAvailabilitySearchResults | undefined> => {
  return await httpPost<ChildAvailabilitySearchResults>(
    `${childrenRouteBase}/query`,
    payload,
    corsOptions,
  );
};

export const convertChildHold = async (
  payload: HoldCreateUpdateRequest,
): Promise<ChildHoldResults | undefined> => {
  return await httpPost<ChildHoldResults>(
    `${childrenRouteBase}/holds/convert`,
    payload,
  );
};

export const createUpdateChildHold = async (
  payload: HoldCreateUpdateRequest[],
) => {
  try {
    const childHoldRes = await httpPost<ChildHoldResults>(
      `${childrenRouteBase}/holds`,
      payload,
    );

    if (
      childHoldRes &&
      /**
       * we need to allow 400 errors through here b/c connect returns this status code for things like child isn't available
       */
      // TODO :: determine what other codes may need to pass through here (e.g. 409, 410, etc.)
      //         JMS: For hold creation/updating I don't believe we get 409/410
      //         We could split the logic here to handle communication errors (>=500) different so we don't remove holds
      (isTwoHundredRes(childHoldRes.status) || childHoldRes.status === 400)
    ) {
      return childHoldRes;
    }

    // TODO :: __ErrorFramework__ this needs thought through/fleshed out
    // should these be Hold Fails for AM Hold errors???
    if (payload.length > 1) {
      throw userErrorCartMultiHoldFailed();
    }
    throw userErrorAvailabilityHold({ globalId: payload[0].globalId });
  } catch (error: unknown) {
    if (isUserError(error)) {
      throw error;
    }

    // TODO If the request to place the holds returns a status code we don't expect,
    // it throws the entire request (including the raw response). Perhaps here we
    // could inspect to ignore communication errors unless it's a transaction hold

    // TODO: Do something different?
    throw error;
  }
};

export const removeChildHold = async (
  globalId: string,
  holdId: string,
): Promise<RemoveHoldResponse | undefined> => {
  return await httpDelete<RemoveHoldResponse>(
    `${childrenRouteBase}/${globalId}/holds/${holdId}`,
  );
};

export const removeChildHoldMulti = async (payload: HoldReleaseRequest[]) => {
  return await httpPost<RemoveHoldResponse>(
    `${childrenRouteBase}/holds/release`,
    payload,
  );
};

export const updateChildHold = async (
  payload: HoldCreateUpdateRequest,
): Promise<ChildHoldItem | undefined> => {
  const { globalId, id: holdId } = payload;
  const res = await httpPatch<ChildHoldResults>(
    `${childrenRouteBase}/${globalId}/holds/${holdId}`,
    payload,
  );
  return res?.children?.[0];
};

interface ChildHoldItemExtended extends ChildHoldItem {
  holdExpiration: string;
}
export const extendChildHold = async (
  payload: HoldCreateUpdateRequest,
): Promise<ChildHoldItemExtended | undefined> => {
  const { endDate } = payload;

  const hold = await updateChildHold(payload);
  return (
    hold && {
      code: hold.code,
      globalId: hold.globalId,
      message: hold.message,
      holdId: hold.holdId,
      holdExpiration: endDate || '',
    }
  );
};

export const getSourceCode = async (
  sourceCode: string,
  options: GetOptions = {},
) => {
  return await httpGetWithStatus<SourceCodeLookupResponse>(
    `${sourceCodeRouteBase}/${sourceCode}`,
    options,
  );
};

export const getGivingHistory = async (
  supporterId: string,
  queryParams: AnyObject = {},
): Promise<GivingHistory> => {
  const searchUrl = constructURLWithQueryParams(
    `${supporterRouteBase}/${supporterId}/giving/history`,
    queryParams,
  );
  const { response, statusCode } = await httpGetWithStatus<GivingHistory>(
    searchUrl,
    corsOptions,
  );

  if (statusCode !== 200 || !Array.isArray(response)) {
    throw new Error('Failed to fetch giving history');
  }

  return response;
};

export const getTaxReceiptDoc = async (supporterId: string, year: number) => {
  const { response, statusCode } = await httpGetWithStatus<{
    status: number;
    body: string;
    headers: { [key: string]: string };
    isBase64Encoded: boolean;
  }>(`${supporterRouteBase}/${supporterId}/tax-receipts/${year}`, {
    ...corsOptions,
  });

  if (statusCode !== 200) {
    throw new Error('Failed to fetch tax receipt');
  }

  return response;
};

export const getTaxReceipts = async (
  supporterId: string,
): Promise<TaxReceipts> => {
  const { response, statusCode } = await httpGetWithStatus<TaxReceipts>(
    `${supporterRouteBase}/${supporterId}/tax-receipts`,
    {
      ...corsOptions,
    },
  );

  if (statusCode !== 200 || !Array.isArray(response)) {
    throw new Error('Failed to fetch tax receipts');
  }

  return response;
};
